import { Component, DoCheck, Input } from '@angular/core';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { Skeleton } from 'components/shared/skeleton/skeleton';
import { InputType } from 'enums/InputType';
import { LedgerPostType } from 'enums/LedgerPostType';
import { LedgerPostModel } from 'models/generated/Entities.Accounting.LedgerPost';
import { OrderProductPlanDiscontinuationModel } from 'models/generated/Entities.GormCrm.OrderProductPlanDiscontinuation';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { SupersedableProductPlanDtoModel } from 'models/generated/GormGateway.Models.SupersedableProductPlanDto';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { ProductNamePipe } from 'pipes/product-name.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { CompanyDataProviderFactory } from 'services/company-data-provider';

@Component({
	selector: 'edit-plan-discontinuation',
	templateUrl: 'edit-plan-discontinuation.component.html',
	imports: [Skeleton, Layout, CustomSelectComponent, CustomInputComponent, TranslatePipe]
})
export class EditPlanDiscontinuationComponent implements DoCheck {

	public inputType = InputType;
	public planList?: SelectListItemModel[];
	public maxAdjustmentValue = -1;
	@Input() private allDiscontinuations?: OrderProductPlanDiscontinuationModel[];
	@Input() private allowedProductPlans?: string[];
	@Input() public discontinuation!: OrderProductPlanDiscontinuationModel;
	@Input() public companyId?: string;
	private loadingPlanList = false; // Prevents multiple calls to getAllProductPlansAndAccountingPlans() while loading the list
	private supersedableProductPlans?: SupersedableProductPlanDtoModel[];
	private ledgerPosts?: LedgerPostModel[];

	constructor(private readonly dataProviderFactory: CompanyDataProviderFactory, private readonly productNamePipe: ProductNamePipe) { }

	ngDoCheck() {
		if (this.planList || this.loadingPlanList) return; // Already loaded

		// Determine list of selectable product plans once all necessary information is available
		if (!this.companyId || !this.allDiscontinuations || !this.allowedProductPlans || !this.discontinuation) return;
		this.loadPlanList();
	}

	async loadPlanList() {
		this.loadingPlanList = true;
		const plansAlreadySelected = this.allDiscontinuations!.map(d => d.productPlanId);
		const dataProvider = this.dataProviderFactory.getProviderForCompanyId(this.companyId!);

		const supersedableInfoPromise = dataProvider.getSupersedableProductPlans();
		const productPlans = await dataProvider.getAllProductPlansAndAccountingPlans();
		this.supersedableProductPlans = await supersedableInfoPromise;
		this.ledgerPosts = await dataProvider.getLedgerPostsByProductPlanIds(this.supersedableProductPlans.map(plan => plan.productPlanId!));

		this.planList = this.supersedableProductPlans
			// Exclude suspended plans, unless the plan is included in "allowedProductPlans"
			.filter(plan => this.allowedProductPlans!.indexOf(plan.productPlanId!) >= 0 || !plan.suspended)
			// Exclude plans already selected in order discontinuations, unless it's already selected in the one we're editing
			.filter(plan => plan.productPlanId === this.discontinuation.productPlanId || plansAlreadySelected.indexOf(plan.productPlanId) === -1)
			.map(supersedablePlanInfo => productPlans.find(plan => plan.id === supersedablePlanInfo.productPlanId))
			.map(plan => new SelectListItemModel(plan!, this.productNamePipe.transform(plan, true)));

		this.selectPlan(productPlans.find(plan => plan.id === this.discontinuation.productPlanId), false);
		this.loadingPlanList = false;
	}

	selectPlan(productPlan?: ProductPlanModel, overrideAdjustmentAmount: boolean = true) {
		if (!productPlan)
			return;
		const supersedablePlanInfo = this.supersedableProductPlans?.find(plan => plan.productPlanId === productPlan?.id);
		if (!supersedablePlanInfo) throw new Error('Plan not available as discontinuation');

		const ledgerPosts = this.ledgerPosts
			?.filter(post => post.accountingPlanId === supersedablePlanInfo.accountingPlanId || post.productPlanId === supersedablePlanInfo.productPlanId)
			// Filter only "posted" ledger posts
			.filter(post => post.ledgerHeader?.postedDate || post.type == LedgerPostType.MigratedInvoice || post.type == LedgerPostType.CounterPost || post.type == LedgerPostType.EqualizingCounterPost);
		const postedAmount = ledgerPosts?.map(post => post.signedValue).reduce((sum, value) => sum + value, 0);

		this.maxAdjustmentValue = postedAmount ?? -1;
		this.discontinuation.productPlan = productPlan;
		this.discontinuation.productPlanId = productPlan?.id;
		this.discontinuation.remainingAmount = supersedablePlanInfo.remainingValue ?? 0;
		if (overrideAdjustmentAmount) this.discontinuation.adjustmentAmount = supersedablePlanInfo?.maxAdjustmentValue ?? 0; // Even though it's the suggested max adjustment value, it's only used as a default value for this purpose
	}
}
